import { STOCK_LEVELS, STOCK_VIEWS, type StockLevel } from "@erp/shared";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { PackageSearch } from "lucide-react";
import { useCallback, useState } from "react";
import { z } from "zod";
import { Badge } from "~/components/ui/badge";
import { DataList, type Column } from "~/components/ui/data-list";
import { Page, PageHeader, Panel, SearchInput, Toolbar } from "~/components/ui/page";
import { Select } from "~/components/ui/select";
import { EmptyState, ErrorState } from "~/components/ui/states";
import { LEVEL_TEXT, LEVEL_TONE, optionLabel, StockTabs } from "~/features/stock/stock";
import { VariantStockSheet, type StockLine } from "~/features/stock/variant-stock-sheet";
import { api, unwrap } from "~/lib/api";
import { cn } from "~/lib/cn";
import { listQuery } from "~/lib/forms";
import { useFormat, useT } from "~/lib/i18n";
import { useInfiniteList } from "~/lib/use-list";

const search = z.object({
  q: z.string().optional(),
  level: z.enum(STOCK_LEVELS).optional(),
  view: z.enum(STOCK_VIEWS).optional(),
  productType: z.string().optional(),
  sort: z.enum(["title", "sku", "available", "onHand"]).optional(),
  dir: z.enum(["asc", "desc"]).optional(),
});
type Search = z.infer<typeof search>;

export const Route = createFileRoute("/_app/stock/")({ validateSearch: search, component: StockLevels });

function fetchStock(params: Search & { offset: number; limit: number }) {
  return unwrap(api.stock.get({ query: listQuery(params) }));
}
type Row = Awaited<ReturnType<typeof fetchStock>>["items"][number];

const ALL = "__all";

function StockLevels() {
  const t = useT();
  const format = useFormat();
  const navigate = useNavigate({ from: Route.fullPath });
  const filters = Route.useSearch();
  const [selected, setSelected] = useState<StockLine | null>(null);
  const list = useInfiniteList("stock", filters, fetchStock);
  const counts = useQuery({
    queryKey: ["stock", "counts", filters.q, filters.productType, filters.view],
    queryFn: () =>
      fetchStock({ q: filters.q, productType: filters.productType, view: filters.view, offset: 0, limit: 1 }).then(
        (r) => r.counts,
      ),
  });
  const types = useQuery({ queryKey: ["products", "types"], queryFn: () => unwrap(api.products.types.get()) });
  const setSearch = useCallback(
    (patch: Partial<Search>) => void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true }),
    [navigate],
  );

  const levelCount = (level: StockLevel | undefined) => {
    const c = counts.data;
    if (!c) return null;
    return level ? c[level] : c.out + c.low + c.ok;
  };

  const columns: Column<Row>[] = [
    {
      id: "product",
      header: t("stock.fields.product"),
      sortKey: "title",
      cell: (r) => (
        <div className="min-w-0 py-1.5">
          <p className="flex items-center gap-2">
            <span className="truncate font-medium">{r.title}</span>
            {r.isKit ? <Badge tone="gold">{t("stock.kit.badge")}</Badge> : null}
          </p>
          <p className="truncate text-xs text-fg-3">
            <span className="tabular">{r.sku}</span>
            {r.options.length ? ` — ${optionLabel(r.options)}` : ""}
          </p>
        </div>
      ),
      className: "max-w-[420px]",
    },
    { id: "onHand", header: t("stock.fields.onHand"), sortKey: "onHand", align: "end", cell: (r) => format.number(r.onHand) },
    {
      id: "reserved",
      header: t("stock.fields.reserved"),
      align: "end",
      cell: (r) => (r.reserved ? format.number(r.reserved) : ""),
      className: "text-fg-2",
      minWidth: "lg",
    },
    {
      id: "available",
      header: t("stock.fields.available"),
      sortKey: "available",
      align: "end",
      cell: (r) => <span className={cn("font-semibold", LEVEL_TEXT[r.level])}>{format.number(r.available)}</span>,
    },
    {
      id: "incoming",
      header: t("stock.fields.incoming"),
      align: "end",
      cell: (r) => (r.incoming ? `+${format.number(r.incoming)}` : ""),
      className: "text-info",
      minWidth: "lg",
    },
    {
      id: "threshold",
      header: t("stock.fields.reorderPoint"),
      align: "end",
      cell: (r) => format.number(r.reorderPoint),
      className: "text-fg-3",
      minWidth: "xl",
    },
    {
      id: "lastCount",
      header: t("stock.fields.lastCount"),
      align: "end",
      cell: (r) => (r.lastCountedAt ? format.date(r.lastCountedAt, "short") : t("stock.neverCounted")),
      className: "text-fg-3",
      minWidth: "xl",
    },
    { id: "level", header: "", cell: (r) => <Badge tone={LEVEL_TONE[r.level]}>{t(`stock.levels.${r.level}`)}</Badge> },
  ];

  const levelTabs: { value: StockLevel | undefined; label: string }[] = [
    { value: undefined, label: t("stock.levels.all") },
    ...STOCK_LEVELS.map((level) => ({ value: level, label: t(`stock.levels.${level}`) })),
  ];

  return (
    <Page>
      <PageHeader title={t("stock.title")} />
      <StockTabs />
      <div className="no-scrollbar mb-4 flex gap-2 overflow-x-auto">
        {levelTabs.map((tab) => {
          const active = filters.level === tab.value;
          const n = levelCount(tab.value);
          return (
            <button
              key={tab.label}
              type="button"
              onClick={() => setSearch({ level: tab.value })}
              aria-pressed={active}
              className={cn(
                "flex h-9 shrink-0 items-center gap-2 rounded-full border px-3.5 text-sm font-medium transition-colors",
                active
                  ? "border-ink bg-ink text-white dark:border-gold dark:bg-gold dark:text-gold-ink"
                  : "border-line-strong bg-surface text-fg-2 hover:text-fg",
              )}
            >
              {tab.label}
              {n !== null ? (
                <span className={cn("tabular text-xs", active ? "opacity-80" : "text-fg-3")}>{format.number(n)}</span>
              ) : null}
            </button>
          );
        })}
      </div>
      <Panel>
        <Toolbar>
          <SearchInput
            value={filters.q ?? ""}
            onChange={(q) => setSearch({ q: q || undefined })}
            placeholder={t("stock.searchPlaceholder")}
          />
          <div className="w-full sm:w-48">
            <Select
              aria-label={t("stock.kit.badge")}
              value={filters.view ?? "articles"}
              onValueChange={(v) => setSearch({ view: v === "articles" ? undefined : (v as Search["view"]) })}
              options={STOCK_VIEWS.map((v) => ({ value: v, label: t(`stock.views.${v}`) }))}
            />
          </div>
          <div className="w-full sm:w-56">
            <Select
              aria-label={t("products.fields.productType")}
              value={filters.productType ?? ALL}
              onValueChange={(v) => setSearch({ productType: v === ALL ? undefined : v })}
              options={[
                { value: ALL, label: t("products.allFamilies") },
                ...(types.data ?? []).map((v) => ({ value: v, label: v })),
              ]}
            />
          </div>
        </Toolbar>
        {list.isError ? (
          <ErrorState error={list.error} onRetry={() => void list.refetch()} />
        ) : (
          <DataList
            rows={list.rows}
            rowKey={(r) => r.variantId}
            columns={columns}
            card={(r) => (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2">
                    <span className="truncate font-medium">{r.title}</span>
                    {r.isKit ? <Badge tone="gold">{t("stock.kit.badge")}</Badge> : null}
                  </p>
                  <p className="truncate text-xs text-fg-3">
                    <span className="tabular">{r.sku}</span>
                    {r.options.length ? ` — ${optionLabel(r.options)}` : ""}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className={cn("font-display text-lg font-medium tabular", LEVEL_TEXT[r.level])}>
                    {format.number(r.available)}
                  </p>
                  {r.reserved ? (
                    <p className="text-xs text-fg-3 tabular">
                      {t("stock.fields.reserved")} {format.number(r.reserved)}
                    </p>
                  ) : null}
                </div>
              </div>
            )}
            onOpen={(r) => setSelected(r)}
            sort={{ key: filters.sort, dir: filters.dir ?? "asc" }}
            onSortChange={(s) => setSearch({ sort: s.key as Search["sort"], dir: s.dir })}
            loading={list.isLoading}
            hasMore={list.hasMore}
            loadingMore={list.loadingMore}
            onLoadMore={list.loadMore}
            empty={
              filters.q || filters.level || filters.productType ? (
                <EmptyState title={t("common.states.noResults")} body={t("common.states.noResultsHint")} />
              ) : filters.view === "kits" ? (
                <EmptyState icon={<PackageSearch />} title={t("stock.kit.emptyKits")} body={t("stock.kit.emptyKitsBody")} />
              ) : (
                <EmptyState icon={<PackageSearch />} title={t("stock.emptyTitle")} body={t("stock.emptyBody")} />
              )
            }
          />
        )}
      </Panel>
      <VariantStockSheet line={selected} onClose={() => setSelected(null)} />
    </Page>
  );
}
